import { occurrenceCalendarRequestScope, assertOccurrenceResponseScope, createEventsClient, readEventIdentifier, readEventListResponse, readEventDetailResponse, readEventMutationResponse, readEventWrappedResponse, readEventHostRequestsResponse, readEventHostAcceptanceResponse, readEventMaterialsResponse } from '@liquid-spirit/api/domains/events';
import { readUploadS3UrlResponse, readUploadMaterialsResponse, UPLOAD_PATHS } from '@liquid-spirit/api/domains/uploads';
import { createVenueRequestsClient } from '@liquid-spirit/api/domains/venue-requests';
import apiClient, { resolveAuthToken } from './apiClient';
import { createCacheAdapter } from './apiCacheAdapter';
import { invalidateEventCaches } from './frontendCacheInvalidation';

const requireToken = (message = 'User is not authenticated. Token missing.') => {
  const token = resolveAuthToken();
  if (!token) throw new Error(message);
  return token;
};

// /api/events/* paths route through the shared events client. Upload path
// constants come from @liquid-spirit/api; file body handling stays app-side.
const events = createEventsClient({
  get: (path, { params, ...options } = {}) => apiClient.get(path, { contract: 'payload', ...options, params }),
  post: (path, body, options = {}) => apiClient.post(path, body, { contract: 'payload', ...options }),
  patch: (path, body, options = {}) => apiClient.patch(path, body, { contract: 'payload', ...options }),
  put: (path, body, options = {}) => apiClient.put(path, body, { contract: 'payload', ...options }),
  delete: (path, options = {}) => apiClient.delete(path, { contract: 'payload', ...options }),
});

export const fetchEvents = async (options = {}) => {
  const { forceFresh = false, signal, token, params = {}, calendarScope } = options;
  // Regional projections bypass the unscoped legacy SWR/persisted list. Their
  // caller owns account, credential, community, zone and day freshness.
  if (Object.hasOwn(params, 'displayTimeZone')) {
    const scope = calendarScope || occurrenceCalendarRequestScope(params);
    const payload = await events.list(params, { contract: 'payload', signal, token });
    return readEventListResponse(assertOccurrenceResponseScope(payload, scope));
  }

  // Authoritative fetch: skip the SWR layer entirely and go straight to the
  // network. Used where the caller owns freshness (react-query queryFns,
  // explicit reloads). The previous forceFresh combo (staleMs:0 +
  // revalidate:false) actually returned the cached snapshot *without* hitting
  // the network — and when SWR did revalidate, it did so in the background and
  // never handed the fresh payload back to the caller — so a wrapping
  // react-query cache would hold a stale (sometimes empty) list until a manual
  // refresh.
  if (forceFresh || Object.keys(params).length) {
    return readEventListResponse(await events.list(params, { contract: 'payload', signal, token }));
  }

  const cacheAdapter = createCacheAdapter({
    key: 'events:list',
    persist: true,
    storageKey: 'events',
    staleMs: 60_000,
    revalidate: true,
    credentialScoped: true,
    validateResponse: readEventListResponse,
  });

  return readEventListResponse(await events.list({}, { cacheAdapter, contract: 'payload', signal, token }));
};

export const createEvent = async (payload) => {
  try {
    const result = readEventMutationResponse(await events.create(payload, { contract: 'payload' }));
    invalidateEventCaches({ eventId: result._id });
    return result;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

const venueRequests = createVenueRequestsClient(apiClient);
export const requestResidenceForEvent = async ({ eventId, householdId, note } = {}) => {
  if (!eventId) throw new Error('eventId is required');
  if (!householdId) throw new Error('householdId is required');
  requireToken();
  const result = await venueRequests.requestEventResidence(eventId, { householdId, note: note ?? '' }, { contract: 'payload' });
  invalidateEventCaches({ eventId });
  return result;
};

export const createCommunityEvent = async (payload) => {
  requireToken();

  const requestBody = {
    ...payload,
    eventType: 'Community',
  };

  try {
    const result = readEventMutationResponse(await events.create(requestBody, { contract: 'payload' }));
    invalidateEventCaches({ eventId: result._id });
    return result;
  } catch (error) {
    console.error('Error creating community event:', error);
    throw error;
  }
};

export const updateEventField = async (eventId, updates) => {
  if (!eventId) {
    throw new Error('Event ID is required to update an event.');
  }
  if (!updates || typeof updates !== 'object' || Array.isArray(updates)) {
    throw new Error('An update payload object is required.');
  }

  requireToken();

  try {
    const result = readEventMutationResponse(await events.update(eventId, updates, { contract: 'payload' }));
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const uploadEventDetailsImage = async (file, eventId) => {
  readEventIdentifier(eventId);
  const ext = file.name.match(/\.[0-9a-z]+$/i)?.[0] || '';
  const fileName = `event-${eventId}-${Date.now()}${ext}`;

  try {
    const { url: signedUrl } = readUploadS3UrlResponse(await apiClient.get(UPLOAD_PATHS.s3Url, {
      params: {
        fileName,
        fileType: file.type,
      },
      contract: 'payload',
    }));

    const uploadRes = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadRes.statusText}`);
    }

    const imgUrl = signedUrl.split('?')[0];

    readEventWrappedResponse(await events.setImage(eventId, { imageUrl: imgUrl }), 'event image update');
    invalidateEventCaches({ eventId });

    return {
      stage: 'completed',
      message: 'Image uploaded and event updated successfully!',
      imgUrl,
    };
  } catch (err) {
    console.error('Error uploading event image:', err);
    return {
      stage: 'error',
      message: err.message || 'An unexpected error occurred during upload.',
    };
  }
};

export const joinEvent = async (eventId, _eventName, user, community, options = {}) => {
  const userCommunityId = community?._id;

  try {
    const token = options.token ?? requireToken();

    const userId = user?._id || user?.id;
    if (!userId) {
      throw new Error('User ID is missing. Cannot join event.');
    }

    const requestBody = {
      attendee: {
        refId: userId,
        type: 'User',
      },
      communityId: userCommunityId || 'Unknown Community',
    };

    const { response, payload } = await events.join(eventId, requestBody, { contract: 'raw', token });
    readEventWrappedResponse(payload, 'event join');

    invalidateEventCaches({ eventId });
    return response;
  } catch (error) {
    console.error('Error joining event:', error);
    throw error;
  }
};

export const leaveEvent = async (eventId, _eventName, user, community, options = {}) => {
  const userCommunityId = community?._id;

  try {
    const token = options.token ?? requireToken();

    const userId = user?._id || user?.id;
    if (!userId) {
      throw new Error('User ID is missing. Cannot join event.');
    }

    const requestBody = {
      attendee: {
        refId: userId,
        type: 'User',
      },
      communityId: userCommunityId || 'Unknown Community',
    };

    const { response, payload } = await events.leave(eventId, requestBody, { contract: 'raw', token });
    readEventWrappedResponse(payload, 'event leave');

    invalidateEventCaches({ eventId });
    return response;
  } catch (error) {
    console.error('Error joining event:', error);
    throw error;
  }
};

export const fetchEventDetails = async (eventId, options = {}) => {
  const { forceFresh = false, forceRefresh = false, signal, token } = options;
  const shouldForceRefresh = forceFresh || forceRefresh;
  if (shouldForceRefresh) {
    return readEventDetailResponse(await events.getById(eventId, { contract: 'payload', signal, token }));
  }
  const cacheAdapter = createCacheAdapter({
    key: `events:item:${eventId}`,
    staleMs: 60_000,
    revalidate: true,
    credentialScoped: true,
    validateResponse: readEventDetailResponse,
  });

  return readEventDetailResponse(await events.getById(eventId, { cacheAdapter, contract: 'payload', signal, token }));
};

export const changeEventHost = async (eventId, hosts) => {
  try {
    const result = readEventDetailResponse(await events.setHosts(eventId, hosts, { contract: 'payload' }), 'event hosts update');
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error adding event host(s):', error);
    throw error;
  }
};

export const addEventMaterials = async (id, title, file, fileType) => {
  readEventIdentifier(id);
  const formData = new FormData();
  formData.append('eventId', id);
  formData.append('title', title.toString());
  formData.append('file', file);
  formData.append('fileType', fileType);

  try {
    const result = readUploadMaterialsResponse(await apiClient.post(UPLOAD_PATHS.materials, formData, {
      jsonContentType: false,
      contract: 'payload',
    }));
    invalidateEventCaches({ eventId: id });
    return result;
  } catch (error) {
    console.error('Error adding event material(s):', error);
    throw error;
  }
};

export const removeEventMaterial = async (eventId, materialId) => {
  try {
    const result = readEventMaterialsResponse(await events.removeMaterial(eventId, materialId, { contract: 'payload' }));
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error deleting material:', error);
    throw error;
  }
};

// Host requests: submit, accept, or decline a request to become a host
export const addHostRequest = async (eventId) => {
  try {
    requireToken('User is not authenticated.');
    const result = readEventHostRequestsResponse(await events.requestHost(eventId, { contract: 'payload' }));
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error submitting host request:', error);
    throw error;
  }
};

export const acceptHostRequest = async (eventId, requesterId) => {
  try {
    requireToken('User is not authenticated.');
    const result = readEventHostAcceptanceResponse(await events.decideHostRequest(eventId, requesterId, 'accept', { contract: 'payload' }));
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error accepting host request:', error);
    throw error;
  }
};

export const declineHostRequest = async (eventId, requesterId) => {
  try {
    requireToken('User is not authenticated.');
    const result = readEventHostRequestsResponse(await events.decideHostRequest(eventId, requesterId, 'decline', { contract: 'payload' }), 'event host request decline');
    invalidateEventCaches({ eventId });
    return result;
  } catch (error) {
    console.error('Error declining host request:', error);
    throw error;
  }
};
